/**
 * /agents — overview dashboard. One card per agent (status, enabled toggle,
 * schedule, last run, flagged-today) + a top stat strip. Enable/disable and
 * "Run now" act inline; clicking a card opens its detail page.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  SimpleGrid,
  Button,
  Switch,
  Spinner,
  Center,
  Stat,
  StatLabel,
  StatNumber,
  Badge,
  Icon,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiPlay, FiRefreshCw, FiPlus, FiCpu, FiAlertTriangle } from 'react-icons/fi';
import { callAgents } from '../../hooks/useAgents';
import AgentStatusChip from '../../components/Agents/AgentStatusChip';

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : '—');

export default function AgentsDashboardPage() {
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const toast = useToast();
  const history = useHistory();
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/$/, '');

  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.600', 'gray.400');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callAgents('listAgents');
      setAgents(res.agents || []);
      setStats(res.stats || null);
    } catch (err) {
      toast({ title: 'Load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (agent, enabled) => {
    setBusyId(agent.id);
    try {
      await callAgents('toggleAgent', { id: agent.id, enabled });
      toast({ title: enabled ? 'Agent enabled' : 'Agent disabled', status: 'success', duration: 2500 });
      await load();
    } catch (err) {
      toast({ title: 'Toggle failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setBusyId(null);
    }
  };

  const runNow = async (agent) => {
    setBusyId(agent.id);
    try {
      const res = await callAgents('runNow', { id: agent.id });
      toast({ title: 'Run started', status: 'success', duration: 2500 });
      history.push(`${sectionUrl}/runs/${res.runId}`);
    } catch (err) {
      toast({ title: 'Run failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box bg={pageBg} minH="100%" p={6}>
      <HStack justify="space-between" mb={5} flexWrap="wrap" spacing={3}>
        <Box>
          <Heading size="lg">Agents</Heading>
          <Text fontSize="sm" color={subtle}>Always-on AI agents working in your Google Workspace.</Text>
        </Box>
        <HStack>
          <Button size="sm" leftIcon={<FiPlus />} colorScheme="blue" onClick={() => history.push(`${sectionUrl}/agents`)}>
            Manage / Create
          </Button>
          <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={load} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </HStack>

      {stats && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
          <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4}>
            <StatLabel color={subtle}>Agents</StatLabel>
            <StatNumber>{agents.length}</StatNumber>
          </Stat>
          <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4}>
            <StatLabel color={subtle}>Enabled</StatLabel>
            <StatNumber>{agents.filter((a) => a.enabled).length}</StatNumber>
          </Stat>
          <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4}>
            <StatLabel color={subtle}>Findings (new)</StatLabel>
            <StatNumber>{stats.new || 0}</StatNumber>
          </Stat>
          <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={4}>
            <StatLabel color={subtle}>Flagged (24h)</StatLabel>
            <StatNumber>{stats.last24h || 0}</StatNumber>
          </Stat>
        </SimpleGrid>
      )}

      {loading && !agents.length ? (
        <Center py={20}><Spinner color="blue.400" /></Center>
      ) : (
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {agents.map((agent) => (
            <Box
              key={agent.id}
              bg={cardBg}
              borderWidth="1px"
              borderColor={cardBorder}
              borderRadius="lg"
              p={5}
              _hover={{ borderColor: 'blue.400', cursor: 'pointer' }}
              onClick={() => history.push(`${sectionUrl}/agents/${agent.id}`)}
            >
              <HStack justify="space-between" align="start" mb={2}>
                <HStack spacing={3}>
                  <Icon as={agent.type === 'phishing-monitor' ? FiAlertTriangle : FiCpu} boxSize={6} color="blue.400" />
                  <Box>
                    <Heading size="sm">{agent.name}</Heading>
                    <Text fontSize="xs" color={subtle}>{agent.type} · every {agent.schedule_minutes}m</Text>
                  </Box>
                </HStack>
                <Box onClick={(e) => e.stopPropagation()}>
                  <Switch
                    isChecked={agent.enabled}
                    onChange={(e) => toggle(agent, e.target.checked)}
                    isDisabled={busyId === agent.id}
                    colorScheme="green"
                  />
                </Box>
              </HStack>
              <Text fontSize="sm" color={subtle} noOfLines={2} mb={3} minH="40px">{agent.description}</Text>
              <HStack justify="space-between" flexWrap="wrap">
                <HStack spacing={2}>
                  <Badge colorScheme={agent.enabled ? 'green' : 'gray'} variant="subtle">{agent.enabled ? 'enabled' : 'off'}</Badge>
                  {agent.config?.actionMode && (
                    <Badge colorScheme="purple" variant="outline">mode: {agent.config.actionMode}</Badge>
                  )}
                </HStack>
                <Text fontSize="xs" color={subtle}>Last run: {fmtDate(agent.last_run_at)}</Text>
              </HStack>
              <HStack mt={4} spacing={2} onClick={(e) => e.stopPropagation()}>
                <Button size="xs" leftIcon={<FiPlay />} colorScheme="blue" variant="outline" onClick={() => runNow(agent)} isLoading={busyId === agent.id}>
                  Run now
                </Button>
                <Button size="xs" variant="ghost" onClick={() => history.push(`${sectionUrl}/agents/${agent.id}`)}>
                  Configure
                </Button>
              </HStack>
            </Box>
          ))}
          {!agents.length && (
            <Center py={16} gridColumn="1 / -1">
              <VStack>
                <Text color={subtle}>No agents yet.</Text>
                <Button size="sm" colorScheme="blue" onClick={() => history.push(`${sectionUrl}/agents`)}>Create one</Button>
              </VStack>
            </Center>
          )}
        </SimpleGrid>
      )}
    </Box>
  );
}
